import { DatePipe } from "@angular/common";
import { MatDateFormats, NativeDateAdapter } from "@angular/material/core";
import { AppConstants } from "../constants/constants";
import { DateConstants, TimeConstants, TimeFormatConstants } from "../constants/TimeConstants";
import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { Platform } from "@angular/cdk/platform";
import { REGEX_TIMESTRING_HH_MM_SS, REGEX_TIMESTRING_HHHH_MM_SS, REGEX_TIMESTRING_MM_SS_T } from "../constants/CommonRegex";

@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  format(date: Date, displayFormat: Object): string {
    if (displayFormat === "input") {
      let day: string = date.getDate().toString();
      day = +day < 10 ? "0" + day : day;
      let month: string = (date.getMonth() + 1).toString();
      month = +month < 10 ? "0" + month : month;
      let year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return date.toDateString();
  }  
}

export const APP_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: { month: "short", year: "numeric", day: "numeric" },
  },
  display: {
    dateInput: "input",
    monthYearLabel: "MMMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

export function formatDateToDDMMYYYY(date: Date): string {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export const DDMMYYYY_FORMAT = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "DD/MM/YYYY",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

export function setTimeFormat(timeString: string, langId: string) {
  var hrText: string = TimeConstants.HOUR;
  var minText: string = TimeConstants.MINUTE;
  var secText: string = TimeConstants.SECOND;

  var datePipe = new DatePipe(AppConstants.DEFAULT_LANG_CODE);
  var n = timeString.search(":");
  var hrPartString = timeString.substring(0, n);
  var hrPart = Number(hrPartString);

  var str = timeString.substring(n + 1, timeString.length);
  var n = str.search(":");
  var minPartString = str.substring(0, 2);
  var minPart = Number(minPartString);

  var str = str.substring(n + 1, str.length);
  var n = str.search(":");
  var secPartString = str.substring(0, 2);
  var secPart = Number(secPartString);

  var time = new Date();
  var timeFormat: string;

  if (hrPart == 0 && minPart == 0 && secPart != 0) {
    time.setSeconds(secPart);

    secText = pluralizeText(secPart, secText, langId);

    timeFormat =
      datePipe.transform(time, `${TimeFormatConstants.SECOND} `) + secText;
  } else if (hrPart == 0 && secPart == 0 && minPart != 0) {
    time.setMinutes(minPart, secPart);

    minText = pluralizeText(minPart, minText, langId);

    timeFormat =
      datePipe.transform(time, `${TimeFormatConstants.MINUTE} `) + minText;
  } else if (hrPart == 0 && minPart != 0 && secPart != 0) {
    time.setMinutes(minPart, secPart);

    secText = pluralizeText(secPart, secText, langId);
    minText = pluralizeText(minPart, minText, langId);

    timeFormat =
      datePipe.transform(time, `${TimeFormatConstants.MINUTE} `) +
      minText +
      datePipe.transform(time, ` ${TimeFormatConstants.SECOND} `) +
      secText;
  } else if (hrPart != 0 && minPart == 0 && secPart == 0) {
    time.setHours(hrPart, minPart, secPart);

    hrText = pluralizeText(hrPart, hrText, langId);

    timeFormat =
      datePipe.transform(time, `${TimeFormatConstants.HOUR} `) + hrText;
  } else if (hrPart != 0 && minPart != 0 && secPart == 0) {
    time.setHours(hrPart, minPart, secPart);

    minText = pluralizeText(minPart, minText, langId);
    hrText = pluralizeText(hrPart, hrText, langId);

    timeFormat =
      datePipe.transform(time, `${TimeFormatConstants.HOUR} `) +
      hrText +
      datePipe.transform(time, ` ${TimeFormatConstants.MINUTE} `) +
      minText;
  } else {
    time.setHours(hrPart, minPart, secPart);

    secText = pluralizeText(secPart, secText, langId);
    minText = pluralizeText(minPart, minText, langId);
    hrText = pluralizeText(hrPart, hrText, langId);

    timeFormat =
      datePipe.transform(time, `${TimeFormatConstants.HOUR} `) +
      hrText +
      datePipe.transform(time, ` ${TimeFormatConstants.MINUTE} `) +
      minText +
      datePipe.transform(time, ` ${TimeFormatConstants.SECOND} `) +
      secText;
  }

  return timeFormat;
}

export function pluralizeText(
  input: number,
  customPluralForm: string,
  langId: string
) {
  if (langId === AppConstants.WELSH_LANG_CODE) return customPluralForm;

  return input > 1 ? customPluralForm + "s" : customPluralForm;
}

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  constructor(
    @Inject(LOCALE_ID) protected override locale: string,
    platform: Platform
  ) {
    super(locale, platform);
  }

  override parse(value: any): Date | null {
    if (typeof value === "string" && value.includes("/")) {
      const str = value.split("/");
      const day = Number(str[0]);
      const month = Number(str[1]) - 1;
      const year = Number(str[2]);
      return new Date(year, month, day);
    }
    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {

    if (displayFormat === DateConstants.DD_MMMM_YYYY) {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${this._to2digit(day)}/${this._to2digit(month)}/${year}`;
    }

    return this.locale == AppConstants.WELSH_LANG_CODE
      ? `${this.getDayOfWeekNames("short")[date.getDay()]}, ${this._to2digit(date.getDate())} ${this.getMonthNames("long")[date.getMonth()]} ${date.getFullYear()}`
      : date.toDateString();
  }

  private _to2digit(n: number): string {
    return ("00" + n).slice(-2);
  }

  override getDayOfWeekNames(style: "long" | "short" | "narrow"): string[] {
    if (this.locale == AppConstants.WELSH_LANG_CODE) {
      const longWeekdays = [
        "Dydd Sul",
        "Dydd Llun",
        "Dydd Mawrth",
        "Dydd Mercher",
        "Dydd Iau",
        "Dydd Gwener",
        "Dydd Sadwrn",
      ];
      const shortWeekdays = ["Sul", "Llun", "Maw", "Mer", "Iau", "Gwe", "Sad"];
      const narrowWeekdays = ["S", "Ll", "M", "M", "I", "G", "S"];

      if (style === "long") return longWeekdays;
      if (style === "short") return shortWeekdays;
      return narrowWeekdays;
    } else {
      return super.getDayOfWeekNames(style);
    }
  }

  override getMonthNames(style: "long" | "short" | "narrow"): string[] {
    if (this.locale == AppConstants.WELSH_LANG_CODE) {
      const longMonths = [
        "Ionawr", "Chwefror", "Mawrth", "Ebrill", "Mai", "Mehefin", 
        "Gorffennaf", "Awst", "Medi", "Hydref", "Tachwedd", "Rhagfyr",
      ];
      const shortMonths = [
        "Ion", "Chwe", "Maw", "Ebr", "Mai", "Meh", "Gor",
        "Aws", "Med", "Hyd", "Tach", "Rhag",
      ];
      const narrowMonths = [
        "I", "Ch", "M", "E", "M", "Me", "G", "A",
        "M", "H", "T", "R",
      ];

      if (style === "long") return longMonths;
      if (style === "short") return shortMonths;

      return narrowMonths;
    } else {
      return super.getMonthNames(style);
    }
  }
}

export function getYearDifference(fromDate: Date, toDate: Date): number {
  return (toDate.getFullYear() - fromDate.getFullYear()) + 
  (toDate.getMonth() - fromDate.getMonth()) / 12 + 
  (toDate.getDate() - fromDate.getDate()) / 365;
}

export function getFormattedTimeString(time: number): string {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  
  return (`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
}

export function convertTimeStringToSeconds(timeString: string): number {
  let totalSeconds = 0;
  
  if(timeString.match(REGEX_TIMESTRING_MM_SS_T)){
    const timeArray = timeString.match(REGEX_TIMESTRING_MM_SS_T);

    totalSeconds = (Number(timeArray[1]) * 60) + Number(timeArray[2]) + (Number(timeArray[3]) / 10);
  }
  else if(timeString.match(REGEX_TIMESTRING_HHHH_MM_SS) || timeString.match(REGEX_TIMESTRING_HH_MM_SS))
  {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    totalSeconds = (hours * 3600) + (minutes * 60) + seconds
  }
  
  return totalSeconds;
}